import { ElementRef, Injectable, OnDestroy } from '@angular/core';
import { Subject, combineLatest, first, takeUntil } from 'rxjs';
import { TagCommanderService } from 'ngx-tag-commander';

import { Product } from '@spartacus/core';

import { DeviceService, ScrollSpyService, SizesEnum } from '@seb/web-design-system/core';
import { IRecipeCard } from '@seb/web-design-system/recipe-card';

import '../models/augmented-spartacus-core.model';

import { DatalayerService } from './datalayer.service';
import { CartGroupEntry } from '../models/cart-group-entry.model';
import { EventProductOrBundle } from '../models/tracked-item.model';
import { Bundle } from '../models/bundle.model';
import { ProductTypeEnum } from '../enums/product-type.enum';
import { StockStatusEnum } from '../enums/stock-status.enum';

@Injectable({
  providedIn: 'root'
})
export class TrackingService implements OnDestroy {
  protected readonly destroy$: Subject<void> = new Subject();
  private isDesktopLarge: boolean;
  private isMobile: boolean;
  private isTablet: boolean;

  constructor(
    private readonly scrollSpyService: ScrollSpyService,
    private readonly tagCommanderService: TagCommanderService,
    private readonly datalayerService: DatalayerService,
    private readonly deviceService: DeviceService
  ) {
    combineLatest([
      this.deviceService.isDesktopLarge, this.deviceService.isTablet, this.deviceService.isMobile
    ]).pipe(
      takeUntil(this.destroy$)
    ).subscribe(([isDesktopLarge, isTablet, isMobile]) => {
      this.isDesktopLarge = isDesktopLarge;
      this.isTablet = isTablet;
      this.isMobile = isMobile;
    });
  }

  static getRecipeAuthor(recipe: IRecipeCard): string {
    const authors = { CHEF: 'CHEF', BRAND: recipe.user.text };
    return authors[recipe.user?.type.toUpperCase()] || 'Individual Author';
  }

  mapToBundleEventClickFromCartGroupEntry(bundleCartGroupEntry: CartGroupEntry, quantity?: number): EventProductOrBundle {
    const mainProduct: Product = bundleCartGroupEntry.entries?.[0]?.product;
    let bundleEvent = {};

    if (bundleCartGroupEntry) {
      bundleEvent = {
        productID: bundleCartGroupEntry.externalReferenceId ? bundleCartGroupEntry.externalReferenceId : '',
        productName: bundleCartGroupEntry.label ? bundleCartGroupEntry.label : '',
        marketingCategory: mainProduct?.topCategoryName ? mainProduct?.topCategoryName : '',
        marketingSubcategory: mainProduct?.subCategoryName ? mainProduct?.subCategoryName : '',
        sisCategory: mainProduct?.sisCategory ? mainProduct?.sisCategory : '',
        sisSubcategory: mainProduct?.sisSubCategory ? mainProduct?.sisSubCategory : '',
        marketingRange: '',
        productType: ProductTypeEnum.BUNDLE,
        productAvailability: !mainProduct?.onSale ? 'not on sale' : (mainProduct['stock'] as number) > 0 ? 'yes' : 'no',
        productPrice: bundleCartGroupEntry.totalPrice?.value ? bundleCartGroupEntry.totalPrice?.value : '',
        productCurrency: bundleCartGroupEntry.totalPrice?.currencyIso
          ? bundleCartGroupEntry.totalPrice?.currencyIso
          : '',
        productBrand: bundleCartGroupEntry['brand'] ? bundleCartGroupEntry['brand'] : '',
        productQuantity: quantity ? quantity : bundleCartGroupEntry.selectedQuantity,
        pageType: this.getPageType()
      };
    }

    return bundleEvent;
  }

  sendEvent(eventLabel: string, event: Record<string, any>): void {
    this.datalayerService.isScriptLoaded().pipe(
      first((value) => value)
    ).subscribe(() => {
      this.tagCommanderService.triggerEvent(eventLabel, undefined, event);
    });
  }

  bundleToEventProduct(bundle: Bundle, index?: number, quantity?: number, context?: string): EventProductOrBundle {
    let trackedBundle: EventProductOrBundle = {};

    if (bundle) {
      trackedBundle = {
        productName: bundle.name ? bundle.name : '',
        productID: bundle.id ? bundle.id : '',
        marketingCategory: bundle.products[0]?.topCategoryName ? bundle.products[0]?.topCategoryName : '',
        marketingSubcategory: bundle.products[0]?.subCategoryName ? bundle.products[0]?.subCategoryName : '',
        sisCategory: bundle.products[0]?.sisCategory ? bundle.products[0]?.sisCategory : '',
        sisSubcategory: bundle.products[0]?.sisSubCategory ? bundle.products[0]?.sisSubCategory : '',
        productType: ProductTypeEnum.BUNDLE,
        productAvailability: !bundle.products[0]?.onSale ? 'not on sale' : bundle.stock > 0 ? 'yes' : 'no',
        productStock: bundle.stock ? bundle.stock : 0,
        productPrice: bundle.discountedPrice?.value ?? bundle.price?.value ?? 0,
        productCurrency: bundle.price?.currencyIso ? bundle.price?.currencyIso : '',
        productBrand: bundle.brand ? bundle.brand : '',
        position: index + 1,
        productLabel: this.datalayerService.getBundleLabel(bundle),
        pageType: this.getPageType()
      };

      if (index !== undefined && index !== null) {
        trackedBundle.position = index + 1;
      }

      if (quantity !== undefined && quantity !== null) {
        trackedBundle.productQuantity = quantity;
      }

      if (context !== undefined && !!context) {
        trackedBundle.position = context;
      }
    }

    return trackedBundle;
  }

  productToEventProduct(product: Product, index?: number, quantity?: number, context?: string): EventProductOrBundle {
    let trackedProduct: EventProductOrBundle = {};

    if (product) {
      trackedProduct = {
        productName: product.name ? product.name : '',
        productID: product.code ? product.code : '',
        marketingCategory: product.topCategoryName ? product.topCategoryName : '',
        marketingSubcategory: product.subCategoryName ? product.subCategoryName : '',
        sisCategory: product.sisCategory ? product.sisCategory : '',
        sisSubcategory: product.sisSubCategory ? product.sisSubCategory : '',
        productType: product.isAccessory
          ? product?.productGenre === 'Forfait'
            ? ProductTypeEnum.REPAIR_PACKAGE
            : ProductTypeEnum.ACCESSORIES
          : ProductTypeEnum.PRODUCTS,
        productStock: product.stock?.stockLevel ? product.stock.stockLevel : 0,
        productPrice: product.discountedPrice?.value ?? product.price?.value ?? 0,
        productCurrency: product.price?.currencyIso ? product.price?.currencyIso : '',
        productBrand: product.brand ? product.brand : '',
        productAvailability: !product?.onSale
          ? 'not on sale'
          : product.stock?.stockLevelStatus === StockStatusEnum.OUT_OF_STOCK
            ? 'no'
            : 'yes',
        productLabel: this.datalayerService.getProductLabel(product),
        pageType: this.getPageType()
      };

      if (index !== undefined && index !== null) {
        trackedProduct.position = index + 1;
      }

      if (quantity !== undefined && quantity !== null) {
        trackedProduct.productQuantity = quantity;
      }

      if (context !== undefined && !!context) {
        trackedProduct.position = context;
      }
    }

    return trackedProduct;
  }

  checkingForPromoView(pushCardComponent: ElementRef, isPushPromo: boolean, promotionName: string): boolean {
    if (pushCardComponent) {
      if (isPushPromo && this.scrollSpyService.isElementActive(pushCardComponent)) {
        this.sendEvent('promotionView', { promotionName });
        this.datalayerService.setDataLayerInternalPromotion(promotionName);
        return true;
      }
    }
    return false;
  }

  onPromoClick(isPushPromo: boolean, promotionName: string): void {
    if (isPushPromo) {
      this.sendEvent('promotionClick', { promotionName });
    }
  }

  crossSellProductListView(products: (Bundle | Product)[], allProducts: (Bundle | Product)[]): void {
    const crossSellTrackedProducts: EventProductOrBundle[] = [];

    products.forEach((product) => {
      let indexPosition;
      let _product = product;

      allProducts.filter((productFromAll: Bundle | Product, indexFromAll) => {
        let _productFromAll;
        if (_product.isBundle) {
          _product = product as Bundle;
          _productFromAll = productFromAll as Bundle;
          return _product.id === _productFromAll.id ? (indexPosition = indexFromAll) : null;
        } else {
          _product = product as Product;
          _productFromAll = productFromAll as Product;
          return _product.code === _productFromAll.code ? (indexPosition = indexFromAll) : null;
        }
      });

      return product.isBundle
        ? crossSellTrackedProducts.push(this.bundleToEventProduct(_product as Bundle, indexPosition))
        : crossSellTrackedProducts.push(this.productToEventProduct(_product as Product, indexPosition));
    });

    this.sendEvent('productListView', { productList: crossSellTrackedProducts });
  }

  pushDisplayedInSlider(pushSize: SizesEnum, pushs: any[], sliderPage: number): any[] {
    let numberOfPushsInPage: number;

    switch (pushSize) {
      case SizesEnum.l:
        numberOfPushsInPage = 1;
        break;
      case SizesEnum.m:
        numberOfPushsInPage = this.isMobile ? 1 : 2;
        break;
      case SizesEnum.s:
        numberOfPushsInPage = this.isDesktopLarge ? 3 : this.isTablet ? 2 : 1;
        break;
      case SizesEnum.xs:
        numberOfPushsInPage = this.isDesktopLarge ? 4 : this.isTablet ? 2 : 1;
        break;
      case SizesEnum.xxs:
        numberOfPushsInPage = this.isDesktopLarge ? 6 : this.isTablet ? 3 : 1;
        break;
      default: {
        numberOfPushsInPage = 1;
        break;
      }
    }

    return pushs?.slice(sliderPage * numberOfPushsInPage, (sliderPage + 1) * numberOfPushsInPage);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getPageType(): any {
    return this.datalayerService.isDisabled ? undefined : this.tagCommanderService.getTcVar('page_type');
  }
}
